import { Component, createRef } from "react"
import { InputGroup, Button, Spinner } from "react-bootstrap"
import { BehaviorSubject, Subscription, of, concat } from "rxjs"
import { catchError, debounceTime, filter, map, switchMap } from "rxjs/operators"
import { AsyncTypeahead } from "react-bootstrap-typeahead"
import { FaSearch } from "react-icons/fa"
import LocationQueryGet from "../../api/Location/LocationQueryGet"
import Address from "../../api/Location/LocationModel/Address"

export interface AddressSearchProps {
  onSearch: (query: string | undefined) => void
  isSearching: boolean
  disabledSearch: boolean
  defaultAddress?: string,
  disableSuggestions?: boolean
  isSSO: boolean
}

interface AddressSearchState {
  options: Address[],
  selectedOption?: Address,
  isOptionsLoading: boolean
  defaultAddressInitialized: boolean
}

export default class AddressSearch extends Component<AddressSearchProps, AddressSearchState> {
  state: AddressSearchState = {
    options: [],
    isOptionsLoading: false,
    defaultAddressInitialized: (this.props.defaultAddress || "") === ""
  }
  searchQuery: string = ""
  typeahead = createRef<AsyncTypeahead<Address>>()
  addressesQuery$?: BehaviorSubject<string>
  addressesSubscription?: Subscription

  componentDidMount() {
    this.addressesQuery$ = new BehaviorSubject<string>(this.props.defaultAddress || "")
    this.addressesSubscription = this.addressesQuery$.pipe(
      debounceTime(500),
      filter(query => (query ?? "").trim().length > 0),
      switchMap((query) => concat(
        of({ isOptionsLoading: true }),
        LocationQueryGet(query).pipe(
          map((options) => ({ isOptionsLoading: false, options: options })),
          catchError(() => of({ isOptionsLoading: false }))
        )
      ))
    ).subscribe(
      (state: { isOptionsLoading: boolean, options?: Address[] }) => {
        state.options ?
          this.setState({ isOptionsLoading: state.isOptionsLoading, options: state.options }) :
          this.setState({ isOptionsLoading: state.isOptionsLoading })
          if (!this.state.defaultAddressInitialized && !state.isOptionsLoading) {
            this.setState({ defaultAddressInitialized: true })
            if (state?.options?.length) {
              this.setState({ selectedOption: state?.options[0] })
              this.performSearch();
            }
            else {
              if (this.props.isSSO) {
                this.setState({ selectedOption: {Text: this.props.defaultAddress} })
                this.performSearch();
              }
            }
          }
      },
      () => this.setState({ isOptionsLoading: false })
    )
  }

  componentWillUnmount() {
    this.addressesSubscription && this.addressesSubscription.unsubscribe()
  }

  performSearch = (selected?: Address) => {
    let address = selected || this.selectedPrediction() || this.state.selectedOption
    let query = address ? address.Text : this.searchQuery

    this.typeahead.current?.blur()
    this.props.onSearch(query)
  }

  selectedPrediction = (): Address | null => {
    // We have to know whether some prediction is under focus because Typeahead component shows prediction under focus inside input box.
    // It looks like to the user as he just typed this text and it forces user to think that
    // if he clicks on Search button then it will search by that text.
    const { activeItem } = this.typeahead.current?.state as { activeItem: Address | null }
    return activeItem
  }

  render() {
    return (
      <InputGroup>
        <AsyncTypeahead
          id="addressSearch"
          labelKey={option => option.Text || ""}
          emptyLabel="No Result Found"
          minLength={3}
          placeholder="City and State or Zip or FF Site ID"
          promptText="City and State or Zip or FF Site ID"
          isLoading={this.state.isOptionsLoading}
          onSearch={(query) => !this.props.disableSuggestions && this.addressesQuery$ && this.addressesQuery$.next(query)}
          open={!!this.props.disableSuggestions ? false : undefined}
          options={this.state.options}
          filterBy={() => true}
          ref={this.typeahead}
          onInputChange={input => this.searchQuery = input}
          onKeyDown={(e: Event) => {
            if (e.type === "keydown" && (e as KeyboardEvent).key === "Enter") {
              // Start search on Enter only if no any selections in prompted items
              // If any prompt item is selected then search will be initiated via onChange event
              !this.selectedPrediction() && this.performSearch()
            }
          }}
          onChange={selected => {
            this.setState({ selectedOption: selected[0] })
            // Start search only if an option has been selected
            selected[0] && this.performSearch(selected[0])
          }}
          selected={this.state.selectedOption ? [this.state.selectedOption] : []} />
        <InputGroup.Append>
          <Button
            variant="secondary"
            className="btn-rounded-right"
            disabled={this.props.isSearching || this.props.disabledSearch}
            onClick={() => {
              const selectedPrediction = this.selectedPrediction()
              if (selectedPrediction) {
                // Set prediction in focus as selected.
                this.setState({ selectedOption: selectedPrediction })
                this.performSearch(selectedPrediction)
              } else {
                this.performSearch()
              }
            }}>
            {this.props.isSearching && <Spinner animation="border" size="sm" />}
            {!this.props.isSearching && <FaSearch />}
          </Button>
        </InputGroup.Append>
      </InputGroup>
    )
  }

  static defaultProps = {
    disableSuggestions: true
  }
}
